const BLUEPRINTS = [
  {
    src: "/assets/orchard-blueprint-scale-branded.jpg",
    alt: "Off-grid hybrid fruit forest system integration and ecological directive blueprint",
    title: "Off-Grid Hybrid Fruit Forest System",
    note: "System integration blueprint: 48V battery bank, hybrid lighting grid, living-soil ecology, and climate control running independent of the public grid.",
    testid: "orchard-blueprint-system",
  },
  {
    src: "/assets/orchard-blueprint-system-branded.jpg",
    alt: "Kinetic scale blueprint comparing controlled grow environments",
    title: "Controlled Grow Environments",
    note: "Kinetic scale reference: engineered canopy environments with measured clearance, capacity planning, and environmental control.",
    testid: "orchard-blueprint-scale",
  },
];

const SWALE_DESIGNS = [
  {
    src: "/assets/swale-backyard.jpg",
    alt: "One-acre contour swale and tree placement design with subsurface math and placement zones",
    title: "One-Acre Contour Swale",
    testid: "swale-design-acre",
    analysis:
      "The master pattern for the acreage: a 208 ft × 208 ft one-acre module cut on contour, fed by up-slope runoff from 39 unmanaged acres. A machine-cut trench (4 ft wide, 18 in deep, level base) charges a subsurface water plume lens beneath a 24-inch uncompacted berm — storing seasonal rainfall in the soil itself rather than letting it leave the land.",
    placement:
      "Trees are placed by hydrology: Medjool date palms crown the dry apex where deep roots tap the plume, pomegranates hold the flanks, hardy mulberries windbreak the mid swale over peaches and cherries, and a multi-layer canopy — nut tree, fruit tree, shrub, groundcover — closes the bottom swale. A 25 ft alley preserves tractor and grazing access, and overflow terminates in an aquaponics and livestock watering tank. Nothing leaves the system.",
  },
  {
    src: "/assets/swale-acre.jpg",
    alt: "Backyard swale design with subsurface math and plant placement matrix",
    title: "Backyard Swale Design",
    testid: "swale-design-backyard",
    analysis:
      "The same mathematics scaled to a hand-built plot: an 8% slope is mapped with the A-frame contour method, and a wheelbarrow-width trench (2 ft wide, 10 in deep) filled with woodchip sponge intercepts up-slope runoff. The trench itself is never planted — avoiding wet-feet risk — while a rock-lined level spillway set 2 inches below the berm top provides controlled emergency drainage that prevents washout.",
    placement:
      "The tree placement matrix assigns species by moisture zone: Medjool date palm on the dry apex (heat-loving, deep aggressive roots), peach and pomegranate on the well-drained mid-berm slopes (drainage sensitive), and cherry and mulberry at the moist base (vigorous drinkers needing consistent moisture).",
  },
];

const LAND_PHOTOS = [
  {
    src: "/assets/land-3.jpeg",
    alt: "Lush fruit forest acreage with palms on Institute land",
    caption: "Fruit Forest Acreage",
    testid: "land-photo-1",
  },
  {
    src: "/assets/land-2.jpeg",
    alt: "Open Institute land with native tree stands",
    caption: "Institute Land",
    testid: "land-photo-2",
  },
  {
    src: "/assets/land-1.jpeg",
    alt: "Member walking the Institute land at the gate",
    caption: "Walking the Land",
    testid: "land-photo-3",
  },
  {
    src: "/assets/land-4-dates.webp",
    alt: "Date palm heavy with clusters of dates in the fruit forest",
    caption: "Date Palms in Fruit",
    testid: "land-photo-4",
  },
];

const Orchard = () => {
  return (
    <section
      id="orchard"
      data-testid="orchard-section"
      className="py-24 px-6 border-b border-stone-800 bg-stone-900/30"
    >
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-4">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Agriculture &amp; Horticulture
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-100 display-serif">
              The Institute Fruit Forests
            </h2>
            <p className="text-stone-400 text-sm leading-relaxed">
              Self-sufficiency begins with the land. The Institute stewards 350 acres with 200
              fruit trees, pairing open-acre food forests with engineered, off-grid grow
              environments — designed, powered, and maintained by the membership.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-stone-950 border border-stone-800">
                <span className="block text-2xl font-extrabold text-amber-400 display-serif">
                  350
                </span>
                <span className="text-xs text-stone-400">Acres of Institute land</span>
              </div>
              <div className="p-4 rounded-xl bg-stone-950 border border-stone-800">
                <span className="block text-2xl font-extrabold text-amber-400 display-serif">
                  200
                </span>
                <span className="text-xs text-stone-400">Fruit trees under cultivation</span>
              </div>
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3 text-xs text-stone-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                <span>
                  <strong className="text-stone-300">Off-grid by design:</strong> battery-banked
                  power, independent lighting and climate systems — no reliance on public
                  utilities.
                </span>
              </div>
              <div className="flex items-start gap-3 text-xs text-stone-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                <span>
                  <strong className="text-stone-300">Living soil ecology:</strong> mycorrhizal
                  networks and beneficial colonies cultivated as part of the growing system.
                </span>
              </div>
              <div className="flex items-start gap-3 text-xs text-stone-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                <span>
                  <strong className="text-stone-300">Taught, not just grown:</strong> members learn
                  horticulture hands-on as part of the Health pillar.
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid gap-6">
            {BLUEPRINTS.map((bp) => (
              <figure
                key={bp.testid}
                data-testid={bp.testid}
                className="rounded-xl border border-stone-800 bg-stone-950 p-4 space-y-3"
              >
                <div className="rounded-lg border border-stone-800 overflow-hidden">
                  <img
                    src={bp.src}
                    alt={bp.alt}
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </div>
                <figcaption className="space-y-1">
                  <div className="text-sm font-bold text-stone-100">{bp.title}</div>
                  <div className="text-[11px] text-stone-400 leading-relaxed">{bp.note}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <a
          href="/growlab"
          data-testid="growlab-feature-card"
          className="block rounded-xl border border-amber-500/30 bg-stone-950 overflow-hidden group hover:border-amber-400/60 transition-colors duration-300"
        >
          <div className="grid lg:grid-cols-2">
            <div className="relative overflow-hidden">
              <video
                src="/assets/growlab/video/hero-flythrough.mp4"
                poster="/assets/growlab/uhd/hero.jpg"
                autoPlay
                loop
                muted
                playsInline
                data-testid="growlab-feature-video"
                className="w-full h-full min-h-[260px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <span className="absolute top-3 left-3 px-2 py-1 rounded bg-stone-950/85 border border-amber-500/40 mono-tag text-[9px] uppercase tracking-widest text-amber-400">
                Interactive 3D Kinetic Model
              </span>
            </div>
            <div className="p-6 sm:p-8 space-y-4">
              <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
                Controlled Environment · Tropical Program
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-100 display-serif">
                CLOUDLAB 811 — The Indoor Tropical Ecosystem
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Mango, cacao, avocado, tamarind, and banana growing as a living-soil ecosystem
                inside a fully instrumented tent — exhaust, intake, circulation, humidity,
                and full-spectrum lighting run by one controller. Explore the kinetic 3D
                model: every piece of equipment labeled, animated, and explained in motion,
                with photoreal renders of each unit.
              </p>
              <span
                data-testid="growlab-feature-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-amber-400 text-stone-950 font-mono text-xs tracking-widest font-bold group-hover:bg-amber-300 transition-colors duration-200"
              >
                OPEN THE KINETIC MODEL →
              </span>
            </div>
          </div>
        </a>

        <div className="space-y-8">
          <div className="space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Design Analysis
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-stone-100 display-serif">
              Water &amp; Contour Engineering
            </h3>
            <p className="text-xs text-stone-400 max-w-2xl leading-relaxed">
              Every tree in the fruit forests is placed by mathematics, not guesswork. These
              engineering studies govern how the Institute harvests rainfall, builds soil, and
              assigns each species to its hydrological zone.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {SWALE_DESIGNS.map((design) => (
              <figure
                key={design.testid}
                data-testid={design.testid}
                className="rounded-xl border border-stone-800 bg-stone-950 p-4 space-y-3"
              >
                <div className="rounded-lg border border-stone-800 overflow-hidden">
                  <img
                    src={design.src}
                    alt={design.alt}
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </div>
                <figcaption className="space-y-2">
                  <div className="text-sm font-bold text-stone-100">{design.title}</div>
                  <p className="text-[11px] text-stone-400 leading-relaxed">{design.analysis}</p>
                  <p className="text-[11px] text-stone-400 leading-relaxed border-t border-stone-800/70 pt-2">
                    <span className="text-amber-400/90 font-semibold">Placement logic: </span>
                    {design.placement}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <h3 className="text-xl font-bold text-stone-100 display-serif">From the Land</h3>
            <span className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">
              Photographed on Institute acreage
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {LAND_PHOTOS.map((photo) => (
              <figure
                key={photo.testid}
                data-testid={photo.testid}
                className="rounded-xl border border-stone-800 bg-stone-950 p-3 space-y-2 group"
              >
                <div className="aspect-[3/4] rounded-lg border border-stone-800 overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <figcaption className="text-[11px] text-stone-400 font-medium px-1 pb-1">
                  {photo.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Orchard;
